import StripeAddressForm, {
  StripeAddressChangeEvent,
} from '@c/forms/StripeAddressForm';
import FormInput from '@c/forms/controls/FormInput';
import FormSelect from '@c/forms/controls/FormSelect';
import FormTextarea from '@c/forms/controls/FormTextarea';
import { PencilSimpleIcon, SecuritySafeIcon, WarningIcon } from '@c/icons';
import DynamicToast from '@c/toasts/DynamicToast';
import MobileBannerChoiceModal from '@c/modals/MobileBannerChoiceModal';
import { zodResolver } from '@hookform/resolvers/zod';
import { Elements } from '@stripe/react-stripe-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@ui/Button';
import { Divider } from '@ui/Divider';
import { RequiredTag } from '@ui/RequiredTag';
import SafeImage from '@ui/SafeImage';
import Spinner from '@ui/Spinner';
import {
  DESKTOP_PREVIEW_MIN_REFERENCE_WIDTH_PX,
  getStorefrontBannerAspectRatio,
  PHONE_PREVIEW_ASPECT_RATIO,
  STOREFRONT_BANNER_DESKTOP_HEIGHT_PX,
  STOREFRONT_BANNER_MAX_WIDTH_PX,
} from '@util/bannerDimensions';
import {
  deleteStoreBannerImage,
  uploadStoreImage,
  uploadStoreMobileImage,
  uploadUserImage,
} from '@util/firestore/listing';
import { redactPhonesAndEmailsFromStr } from '@util/firestore/messages';
import {
  canChangeUserName,
  getNexusStates,
  removeUserKey,
  updateUserByKey,
  updateUserDoc,
} from '@util/firestore/users';
import { STRIPE_ELEMENT_OPTIONS } from '@util/get-stripe';
import { IMAGE_EXT, IMAGE_PATH } from '@util/index';
import {
  AddressDocument,
  addressDocumentSchema,
} from '@util/types/firestore/address';
import { useAuth } from 'context/AuthContext';
import { logError } from '@util/logError';
import { useStripeContext } from 'context/StripeContext';
import { useToastContext } from 'context/ToastContext';
import Link from 'next/link';
import React, { useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import dWeb from '../../headers/sellerprofile/images/shop-seller-header.png';
import { setVacationMode } from '@util/firestore/products';
import Checkbox from '@ui/Checkbox';
import { generateAccountLink } from 'utils/firestore/users'
import StoreSaleSettings from '@c/dashboard/StoreSaleSettings';

export const DEFAULT_SHIPPING_POLICY = `Shipping Policy

Shipping rates will vary based on the destination and the weight of the package. Most orders will be processed and shipped within 1-3 business days. Delivery times will vary based on the shipping method selected at checkout. If you have any questions about shipping, please contact our customer service team.
`;

export const DEFAULT_RETURN_POLICY = `Return Request Terms & Conditions
We accept returns for any items with issues or incorrect items sent.
Return Requests must be done within 48 Hours days of the delivery date.

General Return Terms & Conditions
All items must be returned within 5 days of return request acceptance, in as-shipped condition with original packaging and no signs of mis-use. The buyer shall assume responsibility for all return shipping costs unless the item was not received as described or defective. Returns only accepted for inaccurate items sent.

Refund - Buyer receives full refund minus any shipping costs, unless otherwise agreed with seller.
`;

const TAX_STATES = [
  { value: 'AL', label: 'Alabama', id: 'AL' },
  { value: 'AZ', label: 'Arizona', id: 'AZ' },
  { value: 'AR', label: 'Arkansas', id: 'AR' },
  // { value: 'CA', label: 'California', id: 'CA' },
  { value: 'CO', label: 'Colorado', id: 'CO' },
  { value: 'CT', label: 'Connecticut', id: 'CT' },
  { value: 'DC', label: 'District of Columbia', id: 'DC' },
  { value: 'FL', label: 'Florida', id: 'FL' },
  { value: 'GA', label: 'Georgia', id: 'GA' },
  { value: 'IN', label: 'Indiana', id: 'IN' },
  { value: 'IA', label: 'Iowa', id: 'IA' },
  // { value: 'KS', label: 'Kansas', id: 'KS' },
  { value: 'KY', label: 'Kentucky', id: 'KY' },
  { value: 'SD', label: 'South Dakota', id: 'SD' },
  { value: 'TN', label: 'Tennessee', id: 'TN' },
  { value: 'TX', label: 'Texas', id: 'TX' },
  { value: 'UT', label: 'Utah', id: 'UT' },
  { value: 'VT', label: 'Vermont', id: 'VT' },
  { value: 'VA', label: 'Virginia', id: 'VA' },
  { value: 'WA', label: 'Washington', id: 'WA' },
  { value: 'LA', label: 'Louisiana', id: 'LA' },
  { value: 'ME', label: 'Maine', id: 'ME' },
  { value: 'MA', label: 'Massachusetts', id: 'MA' },
  { value: 'MI', label: 'Michigan', id: 'MI' },
  { value: 'MN', label: 'Minnesota', id: 'MN' },
  { value: 'NE', label: 'Nebraska', id: 'NE' },
  { value: 'NV', label: 'Nevada', id: 'NV' },
  // { value: 'NY', label: 'New York', id: 'NY' },
  { value: 'NC', label: 'North Carolina', id: 'NC' },
  { value: 'OH', label: 'Ohio', id: 'OH' },
  { value: 'OK', label: 'Oklahoma', id: 'OK' },
  { value: 'PA', label: 'Pennsylvania', id: 'PA' },
  { value: 'RI', label: 'Rhode Island', id: 'RI' },
  { value: 'SC', label: 'South Carolina', id: 'SC' },
  { value: 'WV', label: 'West Virginia', id: 'WV' },
  { value: 'WI', label: 'Wisconsin', id: 'WI' },
  { value: 'HI', label: 'Hawaii', id: 'HI' },
  // { value: 'MD', label: 'Maryland', id: 'MD' },
  // { value: 'NJ', label: 'New Jersey', id: 'NJ' },
  { value: 'MO', label: 'Missouri', id: 'MO' },
  { value: 'NM', label: 'New Mexico', id: 'NM' },
];

const TaxStateList = () => {
  const { data, isLoading } = useQuery(
    ['nexus_states'],
    () => getNexusStates(),
    {
      staleTime: Infinity,
    }
  );

  if (isLoading) {
    return (
      <div className="flex">
        <div className="w-1/2 p-4">
          <ul className="list-disc pl-5">
            <li className="h-[1rem] animate-pulse text-[1.6rem]">
              <div className="h-[1rem] animate-pulse rounded-[10px] bg-gray-500 text-[1.6rem]"></div>
            </li>
            <li className="h-[1rem] animate-pulse text-[1.6rem]">
              <div className="h-[1rem] animate-pulse rounded-[10px] bg-gray-500  text-[1.6rem]"></div>
            </li>
          </ul>
        </div>
        <div className="w-1/2 p-4">
          <ul className="list-disc pl-5">
            <li className="h-[1rem] animate-pulse text-[1.6rem]">
              <div className="h-[1rem] animate-pulse rounded-[10px] bg-gray-500  text-[1.6rem]"></div>
            </li>
            <li className="h-[1rem] text-[1.6rem]">
              <div className="h-[1rem] animate-pulse rounded-[10px] bg-gray-500  text-[1.6rem]"></div>
            </li>
          </ul>
        </div>
      </div>
    );
  }
  if (!data) return null;
  const halfLength = Math.ceil(data.length / 2);
  const leftColumn = data.slice(0, halfLength);
  const rightColumn = data.slice(halfLength);

  return (
    <div className="flex">
      <div className="w-1/2 p-4">
        <ul className="list-disc pl-5">
          {leftColumn.map((state) => (
            <li className="pb-4 text-[1.6rem]" key={state.region}>
              {state.region}
            </li>
          ))}
        </ul>
      </div>
      <div className="w-1/2 p-4">
        <ul className="list-disc pl-5">
          {rightColumn.map((state) => (
            <li className="pb-4 text-[1.6rem]" key={state.region}>
              {state.region}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
interface InputRowProps {
  label: string;
  description?: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
}
const InputRow = ({
  label,
  description,
  required,
  children,
}: InputRowProps) => (
  <div className="flex w-full flex-col items-start gap-[2.4rem] p-[1.6rem]">
    <div className="flex w-full flex-col gap-[1.6rem] lg:flex-row lg:items-start lg:justify-between">
      <div className="flex w-[32rem] flex-col justify-center gap-[0.4rem]">
        <div className="flex items-center gap-[0.8rem]">
          <h3 className="text-[1.6rem] font-semibold leading-[2.2rem] text-brand-light-black">
            {label}
          </h3>
          {required && <RequiredTag />}
        </div>
        <span className="text-[1.3rem] leading-[2rem] text-brand-dark-gray">
          {description}
        </span>
      </div>
      <div className="flex flex-col justify-center gap-[1.6rem] lg:grow">
        {children}
      </div>
    </div>
  </div>
);

const formSchema = z.object({
  storePhoto: z
    .object({
      localUrl: z.string().url().optional(),
      remoteUrl: z.string().url().optional(),
    })
    .optional(),
  storePhotoMobile: z
    .object({
      localUrl: z.string().url().optional(),
      remoteUrl: z.string().url().optional(),
    })
    .optional(),
  userPhoto: z
    .object({
      localUrl: z.string().url().optional(),
      remoteUrl: z.string().url().optional(),
    })
    .optional(),
  username: z.string().optional(),
  address: addressDocumentSchema.optional(),
  bio: z.string().optional(),
  taxStates: z.array(z.string()).optional(),
  shippingPolicy: z.string().optional(),
  returnPolicy: z.string().optional(),
  sellerProfile: z
    .object({
      ebay_username: z.string().optional(),
      instagram: z.string().optional(),
      youtube: z.string().optional(),
      website: z.string().optional(),
      tiktok: z.string().optional(),
      linkedin: z.string().optional(),
      facebook: z.string().optional(),
      fredmiranda: z.string().optional(),
    })
    .optional(),
});

type FormSchema = z.infer<typeof formSchema>;

// GB #1612: banner upload guardrails. Client-side only -- a seller who
// insists on a smaller image can still proceed (we warn, we don't block),
// but the wrong file type or an oversized file is rejected outright.
const ALLOWED_BANNER_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
// GB #1612 -- Codex fifth review, finding 8: some Android and desktop file
// pickers report an EMPTY string for File.type even for a perfectly valid
// image. Checking `image.type` alone rejected those with "Please upload a
// JPG, PNG, or WebP image" -- a file that would have uploaded fine before
// this PR existed. Fall back to the file's extension when the browser
// hasn't told us a MIME type at all.
const ALLOWED_BANNER_IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp'];
function isAllowedBannerImage(file: File): boolean {
  if (file.type) return ALLOWED_BANNER_IMAGE_TYPES.includes(file.type);
  const lowerName = file.name.toLowerCase();
  return ALLOWED_BANNER_IMAGE_EXTENSIONS.some((ext) => lowerName.endsWith(ext));
}
const MAX_BANNER_FILE_SIZE_BYTES = 8 * 1024 * 1024; // 8MB
// Matches the recommended upload size (1600 x 360) stated in the guidance
// copy and the Help Center article. The storefront container renders at
// 1320px, but we recommend 1600 for resolution headroom on sharp screens,
// so the blur warning fires for anything under the recommendation.
const RECOMMENDED_BANNER_WIDTH_PX = 1600;
// Approved mobile banner size (800 x 400) -- chosen to sit in the middle of
// the real mobile screen-width distribution (93.9% of mobile sessions are
// 360-449px wide; near-perfect at 402px).
const RECOMMENDED_MOBILE_BANNER_WIDTH_PX = 800;

// GB #1612: the desktop banner (store_photo) and the optional mobile banner
// (store_photo_mobile) share identical validation -- only the upload
// function, the form field, the Firestore key, and the recommended-size
// copy differ. Config objects let handleShopFileInputChange and
// handleMobileBannerFileInputChange both call the same runBannerUpload.
type BannerUploadConfig = {
  formField: 'storePhoto' | 'storePhotoMobile';
  docKey: 'store_photo' | 'store_photo_mobile';
  uploadFn: (data: Blob, userId: string) => Promise<string>;
  recommendedWidthPx: number;
  recommendedSizeLabel: string;
};
const DESKTOP_BANNER_CONFIG: BannerUploadConfig = {
  formField: 'storePhoto',
  docKey: 'store_photo',
  uploadFn: uploadStoreImage,
  recommendedWidthPx: RECOMMENDED_BANNER_WIDTH_PX,
  recommendedSizeLabel: '1600 x 360',
};
const MOBILE_BANNER_CONFIG: BannerUploadConfig = {
  formField: 'storePhotoMobile',
  docKey: 'store_photo_mobile',
  uploadFn: uploadStoreMobileImage,
  recommendedWidthPx: RECOMMENDED_MOBILE_BANNER_WIDTH_PX,
  recommendedSizeLabel: '800 x 400',
};

function getImageDimensions(
  file: File
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(objectUrl);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Could not read image dimensions.'));
    };
    img.src = objectUrl;
  });
}

// GB #1612 -- Codex P2 (fourth review): the settings page renders inside
// DashboardLayout's content column, which is narrower than the seller's
// actual browser viewport (a fixed side menu + horizontal padding are
// subtracted from it -- see the comment on getStorefrontBannerAspectRatio
// in bannerDimensions.ts). Tracking the REAL viewport width lets the
// preview simulate "what would the storefront look like for a buyer at
// this width," even though the preview box itself is physically narrower.
// Starts at null (unknown) so the server-rendered/pre-hydration markup
// doesn't guess at a window size it cannot see.
//
// GB #1612 -- Codex fifth review, finding 13b: this used to read
// window.innerWidth, which INCLUDES the vertical scrollbar's width when
// one is visible (Windows, some Linux configs -- typically 15-17px). CSS
// media queries (and Tailwind's sm: breakpoint, and the real storefront's
// own layout) use the layout viewport width, which EXCLUDES the
// scrollbar -- that's document.documentElement.clientWidth. In roughly a
// 15px band just above the 640px breakpoint, the two disagreed on which
// side of the breakpoint the browser was on, producing up to a 46% ratio
// error between this preview and the real storefront.
function useViewportWidth(): number | null {
  const [width, setWidth] = useState<number | null>(null);
  useEffect(() => {
    const update = () => setWidth(document.documentElement.clientWidth);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return width;
}

// GB #1612 -- Codex fifth review, finding 4 ("most serious finding of
// twenty"): converts a STORED CDN URL (e.g. userDoc.store_photo_mobile,
// which already has IMAGE_PATH prepended and IMAGE_EXT appended) back into
// the raw Storage fullPath that deleteStoreBannerImage expects -- the
// inverse of the `IMAGE_PATH + fullPath + IMAGE_EXT` construction used
// everywhere a banner URL is built after upload.
function cdnUrlToStorageFullPath(cdnUrl: string): string {
  let path = cdnUrl;
  if (path.startsWith(IMAGE_PATH)) path = path.slice(IMAGE_PATH.length);
  // Guard IMAGE_EXT being empty explicitly: `str.slice(0, -0)` is `''` in
  // JS (negative zero coerces to positive zero for slice's end index), not
  // the original string -- a real correctness trap if this function is
  // ever exercised with an empty extension.
  if (IMAGE_EXT && path.endsWith(IMAGE_EXT)) {
    path = path.slice(0, path.length - IMAGE_EXT.length);
  }
  return path;
}

const ShopSettingsPage = () => {
  const queryClient = useQueryClient();
  const { userDoc, refreshUserDoc } = useAuth();
  const { showToast } = useToastContext();
  const viewportWidth = useViewportWidth();
  // Fallback (pre-hydration, or SSR) matches this PR's PREVIOUS fix's
  // ratio (4.4:1, the 1320px-cap ratio) -- a reasonable placeholder for
  // the brief window before the real width is known client-side, not a
  // silent return to the bug: it is replaced within one effect tick.
  //
  // GB #1612 -- Codex fifth review, finding 12: this used to pass the
  // seller's raw viewport width straight through, with no floor. A seller
  // reviewing this field's own copy ("This is what buyers see on a
  // computer or tablet") from their OWN phone got the MOBILE ratio
  // (1.95) -- rendering identically to the phone preview sitting right
  // below it. This preview specifically simulates a desktop/tablet
  // buyer regardless of which device the seller is using right now, so
  // its input width is floored at DESKTOP_PREVIEW_MIN_REFERENCE_WIDTH_PX.
  const desktopPreviewAspectRatio =
    viewportWidth == null
      ? STOREFRONT_BANNER_MAX_WIDTH_PX / STOREFRONT_BANNER_DESKTOP_HEIGHT_PX
      : getStorefrontBannerAspectRatio(
          Math.max(viewportWidth, DESKTOP_PREVIEW_MIN_REFERENCE_WIDTH_PX)
        );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingLink, setGeneratingLink] = useState(false);
  const [addressElement, setAddressElement] = useState({
    key: 1,
    loadingSave: false,
    showSaveButton: false,
  });
  const [loadingVacation, setLoadingVacation] = useState(false);
  const [loadingOfferSettings, setLoadingOfferSettings] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validatedAddress, setValidatedAddress] =
    useState<AddressDocument | null>(null);
  const { register, handleSubmit, setValue, watch, control } =
    useForm<FormSchema>({
      mode: 'onSubmit',
      resolver: zodResolver(formSchema),
    });

  const { showSuccessToast, showErrorToast } = useToastContext();
  const form = watch();

  const { stripePromise } = useStripeContext();

  // GB #1612 -- Codex P2 fix: these are deliberately NOT react-hook-form
  // state. The immediate "show the seller their own picked image" preview
  // used to live in `form.storePhoto`/`form.storePhotoMobile` itself (set
  // to `{localUrl}` with no `remoteUrl` before the upload even started).
  // If the upload then failed, that incomplete object was never cleared,
  // so `onSubmit` picked it up as `store_photo: undefined` -- which
  // Firestore's updateDoc() rejects, and since onSubmit had no
  // try/catch, `isSubmitting` never reset, leaving the "Update Shop
  // Settings" button stuck spinning with no way out. Splitting the
  // "what the seller sees right now" preview from "what onSubmit can
  // possibly send" means a failed or in-flight upload can never reach
  // onSubmit at all -- form.storePhoto/storePhotoMobile are now ONLY ever
  // set (via setValue) once the upload has actually succeeded, with both
  // localUrl and remoteUrl together, exactly as before this PR.
  const [localPreviews, setLocalPreviews] = useState<
    Partial<Record<'storePhoto' | 'storePhotoMobile', string>>
  >({});
  const [uploadingFields, setUploadingFields] = useState<
    Partial<Record<'storePhoto' | 'storePhotoMobile', boolean>>
  >({});
  const isBannerUploadInFlight =
    !!uploadingFields.storePhoto || !!uploadingFields.storePhotoMobile;

  // GB #1799: state for the upload-time "make skipping the mobile banner
  // explicit" modal. See the trigger inside runBannerUpload's success path.
  const [showMobileBannerChoice, setShowMobileBannerChoice] = useState(false);
  const [mobileBannerChoicePreview, setMobileBannerChoicePreview] =
    useState<string | null>(null);

  // GB #1799 -- adversarial + /qa-review (both independently reproduced):
  // the trigger below used to read mobile-banner presence directly from
  // the closure captured at file-pick time (localPreviews/form/userDoc),
  // which can go stale across the desktop upload's own awaits -- exactly
  // the hazard latestUploadTokenRef/pendingWriteRef/activeObjectUrlsRef
  // above already exist to avoid in this same function. Reproduced
  // scenario: seller has no mobile banner, starts a slow desktop upload,
  // then adds a mobile banner which finishes FIRST -- the desktop
  // upload's stale closure still reads "no mobile banner" when it
  // finishes, and the modal fires claiming something no longer true.
  // Mirrored into a ref (kept current on every render, not just on
  // mount) so runBannerUpload always reads the LATEST value, never a
  // snapshot from when it was called -- the ref object itself is stable
  // across renders, so even an in-flight call's stale closure sees live
  // data through it.
  const hasMobileBannerRef = useRef(false);
  useEffect(() => {
    hasMobileBannerRef.current =
      !!localPreviews.storePhotoMobile ||
      !!form.storePhotoMobile?.localUrl ||
      !!userDoc?.store_photo_mobile;
  });

  // GB #1799 -- Bill's ruling on the adversarial reviewer's M1 finding
  // (verbatim, via AskUserQuestion): "No -- remember the decline for that
  // sitting." Once the seller clicks "This is fine, keep it," the modal
  // stays quiet for the rest of THIS page session, even if they re-upload
  // the desktop banner again -- it still asks fresh on a later visit
  // (component remounts, ref resets), and it's still never mandatory. Set
  // in the modal's onKeepDesktopImage handler below, read in
  // runBannerUpload's trigger alongside hasMobileBannerRef.
  const hasDeclinedMobileBannerThisSessionRef = useRef(false);

  // GB #1612 -- Codex P2 (second review): if a seller picks a second banner
  // for the SAME field while the first upload is still running, both calls
  // proceed (nothing disabled the change button, no request identity was
  // tracked). If the first (now-abandoned) upload happens to resolve AFTER
  // the second, its `setValue`/`updateUserDoc` would overwrite the form and
  // Firestore with the STALE file, even though the preview already shows
  // the newer one -- and either call's `finally` could clear the in-flight
  // flag while the other was still running, partially undoing the P2 fix
  // above. A ref (not state) so the CURRENT value is readable synchronously
  // mid-async-function, which state closures cannot do reliably.
  //
  // Chosen fix: discard stale completions (token/version check), not a
  // blocking queue. A blocking queue would still eventually apply the
  // FIRST (abandoned) file after the second one finishes -- a "resurrected"
  // stale choice is exactly the bug, just delayed. Tokens make the seller's
  // most recent pick the only one that can ever take effect, regardless of
  // which network call happens to finish first, and do not block the UI
  // while a previous upload for the same field is still settling.
  const latestUploadTokenRef = useRef<
    Record<'storePhoto' | 'storePhotoMobile', number>
  >({ storePhoto: 0, storePhotoMobile: 0 });

  // GB #1612 -- Codex P2 (third review): the token check above is not
  // enough. It runs BEFORE dispatching updateUserDoc, but updateUserDoc
  // itself is an independent Firestore write with no ordering guarantee
  // against another in-flight write for the same
  // field. If upload A's Storage step finishes and its updateUserDoc call
  // is already dispatched, then the seller picks B, B's own write can
  // reach the server and persist BEFORE A's does -- leaving the database
  // holding A's stale value while the form and preview already show B,
  // with no error surfaced. A client-side "check before dispatch" cannot
  // fix an ordering problem between two requests already in flight.
  //
  // Fix: serialize the actual PERSISTENCE per field, not just the
  // decision to persist. Each write is chained behind whatever write (if
  // any) is currently pending for that field, so the server can never
  // receive a later write before an earlier one has already been fully
  // applied. Chosen over "detect the conflict after the fact and re-heal"
  // because that would still let the wrong value be visible/read between
  // the conflicting writes; serialized dispatch means the wrong value is
  // never actually sent after the right one already has been.
  const pendingWriteRef = useRef<
    Record<'storePhoto' | 'storePhotoMobile', Promise<unknown>>
  >({ storePhoto: Promise.resolve(), storePhotoMobile: Promise.resolve() });

  // GB #1612 -- Codex fifth review, finding 14: URL.createObjectURL(image)
  // was never revoked -- not on success (the blob URL keeps being
  // displayed instead of switching to the remote CDN URL, so it can't be
  // revoked right after upload), not on failure, not on unmount, not on
  // logout. Each file pick pinned up to 8MB in memory for the rest of the
  // document's lifetime. Tracks the currently-live blob URL per field so a
  // REPLACEMENT (new pick superseding an old, still-referenced blob) can
  // revoke the one it's replacing, and an unmount effect below revokes
  // whatever's left (covers navigation away and logout, since React
  // unmounts this component on route change).
  const activeObjectUrlsRef = useRef<
    Record<'storePhoto' | 'storePhotoMobile', string | null>
  >({ storePhoto: null, storePhotoMobile: null });
  function setActiveObjectUrl(
    field: 'storePhoto' | 'storePhotoMobile',
    url: string | null
  ) {
    const prev = activeObjectUrlsRef.current[field];
    if (prev && prev !== url) {
      URL.revokeObjectURL(prev);
    }
    activeObjectUrlsRef.current[field] = url;
  }
  useEffect(() => {
    return () => {
      Object.values(activeObjectUrlsRef.current).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, []);

  async function runBannerUpload(image: File, config: BannerUploadConfig) {
    if (!userDoc) return;

    if (!isAllowedBannerImage(image)) {
      showErrorToast('Please upload a JPG, PNG, or WebP image.');
      return;
    }

    if (image.size > MAX_BANNER_FILE_SIZE_BYTES) {
      const sizeMb = (image.size / (1024 * 1024))
        .toFixed(1)
        .replace(/\.0$/, '');
      showErrorToast(`That image is ${sizeMb}MB. Please upload one under 8MB.`);
      return;
    }

    // Claim this attempt's identity BEFORE any async work. Any call for
    // the SAME field that starts after this point claims a higher token,
    // and every later check in this call sees itself as stale.
    const myToken = ++latestUploadTokenRef.current[config.formField];
    const isStale = () =>
      latestUploadTokenRef.current[config.formField] !== myToken;

    // GB #1612 -- Codex sixth review, finding 2: capture whatever is
    // CURRENTLY persisted for this field before this attempt can possibly
    // overwrite it. A REPLACEMENT (the seller already has a saved banner
    // and picks a new one) wrote the new URL but never cleaned up the old
    // Storage object -- only the dedicated Remove button (mobile only)
    // did that. Every successfully replaced banner stayed publicly
    // reachable at its old CDN URL forever, which is especially bad if
    // the seller replaced it BECAUSE the old one had something in it they
    // wanted gone. Read from userDoc now, before any await, so this
    // reflects the real last-known-good persisted value regardless of
    // what this attempt itself does next.
    const previousPersistedRemoteUrl = userDoc[config.docKey] ?? null;

    try {
      const { width } = await getImageDimensions(image);
      if (width < config.recommendedWidthPx && !isStale()) {
        showToast(
          <DynamicToast
            text={`This image is ${width}px wide. It may look blurry on large screens. We recommend ${config.recommendedSizeLabel}.`}
          />,
          {
            position: 'bottom-center',
            style: {
              borderRadius: '10px',
              backgroundColor: '#FDF4E9',
              borderStyle: 'solid',
              borderWidth: '1px',
              borderColor: '#C75008',
              width: 'min-content',
            },
          }
        );
      }
    } catch {
      // GB #1612 -- Codex fifth review, finding 6: img.onerror is the
      // STRONGEST signal a file is not a usable image (the browser
      // itself could not decode it) -- swallowing this and letting the
      // upload proceed anyway meant a file that fails every browser's
      // decoder could still become the live banner. Treat it as a hard
      // reject, with the same message as an invalid file type (from the
      // seller's perspective, an undecodable file IS a file-type
      // problem, whatever its claimed extension or MIME type said).
      if (!isStale()) {
        showErrorToast('Please upload a JPG, PNG, or WebP image.');

        // GB #1612 -- Codex sixth review, finding 3: this path returns
        // BEFORE ever reaching the upload try/finally below, which is the
        // only other place that clears uploadingFields for this field. If
        // an EARLIER attempt for this field was still in flight when this
        // pick superseded it (claimed a newer token), that earlier
        // attempt's own `finally` is now barred from clearing
        // uploadingFields (isStale() is true for it) -- and since THIS
        // attempt never reaches its own finally either, nothing ever
        // clears it. The field would show "uploading" forever, the
        // isBannerUploadInFlight guard would permanently block Save, and
        // the abandoned earlier attempt's local preview (if any) would
        // stay on screen -- all until a full page reload. This attempt
        // owns the latest token, so it is responsible for resolving the
        // field back to a clean, non-uploading state, exactly like a
        // failed upload/save does (see the Firestore-write-failure catch
        // below). Safe even when there was no earlier attempt: form/
        // localPreviews only ever hold a value from a save that already
        // succeeded, and clearing them just falls back to the same,
        // still-correct userDoc-persisted value.
        setValue(config.formField, undefined);
        setLocalPreviews((prev) => {
          const next = { ...prev };
          delete next[config.formField];
          return next;
        });
        setActiveObjectUrl(config.formField, null);
        setUploadingFields((prev) => ({ ...prev, [config.formField]: false }));
      }
      return;
    }

    // A newer pick for this field already arrived while we were reading
    // dimensions -- don't let this stale attempt overwrite its preview.
    if (isStale()) return;

    // Show the seller their own picked image right away, before the
    // network round-trip to Storage finishes -- but only as a LOCAL
    // preview, never as submittable form state (see the P2 comment above).
    const localUrl = URL.createObjectURL(image);
    setActiveObjectUrl(config.formField, localUrl);
    setLocalPreviews((prev) => ({ ...prev, [config.formField]: localUrl }));
    setUploadingFields((prev) => ({ ...prev, [config.formField]: true }));

    try {
      const fullPath = await config.uploadFn(image, userDoc.uid);
      if (isStale()) {
        // A newer upload for this field superseded this one while it was
        // in flight. Discard the reference silently (no setValue, no
        // Firestore write, no toast for a choice the seller already
        // moved on from) -- but the file DID land in Storage, so GB
        // #1612 Codex fifth review, finding 4 ("same for every
        // superseded stale upload"): actually delete it, don't just
        // leave it orphaned-but-CDN-reachable. Non-fatal if this fails --
        // an inert Storage object is a smaller problem than the request
        // that's already discarding it throwing.
        deleteStoreBannerImage(fullPath).catch((cleanupErr) => {
          logError(
            cleanupErr,
            undefined,
            `stale upload storage cleanup failed uid=${userDoc.uid} field=${config.formField}`
          );
        });
        return;
      }
      const remoteUrl = IMAGE_PATH + fullPath + IMAGE_EXT;
      // Only NOW -- upload confirmed successful, remoteUrl in hand, and
      // still the most recent pick for this field -- does this become
      // part of the form's submittable state.
      setValue(config.formField, { localUrl, remoteUrl });

      // Chain this write behind whatever write is currently pending for
      // this field (see the pendingWriteRef comment above). Reading and
      // replacing the ref happens synchronously in one go, with no
      // `await` in between, so there is no window for a concurrent call
      // to interleave and race the bookkeeping itself.
      const previousWrite = pendingWriteRef.current[config.formField];
      const thisWrite = previousWrite
        .catch(() => {
          // A prior failed write must not permanently jam the queue for
          // this field -- move on so this write still gets its turn.
        })
        .then(() => {
          if (isStale()) return; // superseded again while waiting our turn
          return updateUserDoc(userDoc.uid, { [config.docKey]: remoteUrl });
        });
      pendingWriteRef.current[config.formField] = thisWrite;
      await thisWrite;

      if (isStale()) return; // no toast for a pick the seller already moved on from
      showToast(<DynamicToast text={'Shop successfully updated.'} />, {
        position: 'bottom-center',
        style: {
          borderRadius: '10px',
          backgroundColor: '#FDF4E9',
          borderStyle: 'solid',
          borderWidth: '1px',
          borderColor: '#C75008',
          width: 'min-content',
        },
      });

      // GB #1799 (CPO ruling, Bill-approved 2026-08-02): the desktop banner
      // just saved successfully. If the seller has no mobile banner, this is
      // the exact moment the CPO's brief describes -- "after a seller
      // uploads or changes their desktop banner" -- so surface the phone
      // preview and make skipping the mobile banner a real, explicit click
      // instead of something that just never comes up. Only fires for the
      // DESKTOP config, and only when no mobile banner exists already
      // (checked via hasMobileBannerRef, current as of this instant, not a
      // stale closure -- see its declaration above); never re-fires on
      // saves that don't touch the desktop banner. Also never re-fires
      // within the same page session once the seller has explicitly
      // declined (hasDeclinedMobileBannerThisSessionRef -- Bill's ruling on
      // the adversarial reviewer's M1 finding: don't ask again in the same
      // sitting, still ask fresh next visit).
      if (
        config.formField === 'storePhoto' &&
        !hasMobileBannerRef.current &&
        !hasDeclinedMobileBannerThisSessionRef.current
      ) {
        // GB #1799 -- adversarial + /qa-review: preview the LOCAL blob
        // (already in scope from this same upload, above), not remoteUrl.
        // Every sibling preview on this page (desktop, phone, mobile,
        // avatar) prefers the local blob for exactly this reason -- a
        // freshly-written CDN URL is a guaranteed-cold pull-zone fetch,
        // and SafeImage silently swaps in a "not found" placeholder on any
        // miss. The local blob is instant and byte-identical to what the
        // seller just picked; no network round-trip, no failure mode.
        setMobileBannerChoicePreview(localUrl);
        setShowMobileBannerChoice(true);
      }

      // GB #1612 -- Codex sixth review, finding 2: only NOW, after the new
      // write is confirmed persisted, delete the OLD Storage object this
      // one just replaced. Ordering matters: this must run strictly after
      // the write succeeds (never before, and never if the write threw),
      // so a failed save can't take down a still-live banner. The
      // inequality check is a defensive guard against ever deleting the
      // object this same call JUST uploaded, in the extremely unlikely
      // case two uploads for this field landed on the same Storage path
      // (same millisecond timestamp). Non-fatal to the seller-facing flow
      // if this specific step fails: the new banner is genuinely live
      // either way.
      if (
        previousPersistedRemoteUrl &&
        previousPersistedRemoteUrl !== remoteUrl
      ) {
        deleteStoreBannerImage(
          cdnUrlToStorageFullPath(previousPersistedRemoteUrl)
        ).catch((cleanupErr) => {
          logError(
            cleanupErr,
            undefined,
            `replaced banner storage cleanup failed uid=${userDoc.uid} field=${config.formField}`
          );
        });
      }
    } catch (err) {
      if (isStale()) return; // don't show an error for an abandoned attempt
      showErrorToast('We could not upload your banner. Please try again.');
      logError(
        err,
        undefined,
        `${config.formField} upload failed uid=${userDoc.uid}`
      );
      // GB #1612 -- Codex fifth review, finding 2: this used to only clear
      // localPreviews. That's correct when the STORAGE upload fails (form
      // state was never touched), but not when the FIRESTORE write fails
      // AFTER setValue(config.formField, {...}) already ran above --
      // form.storePhoto/storePhotoMobile.localUrl survived, so the
      // preview kept showing the image the error toast just said failed,
      // and the Remove button appeared for a field that was never
      // actually saved. Clearing form state too falls back to
      // userDoc.store_photo(_mobile) -- the real, last-known-good
      // persisted value -- which is always correct here: form state only
      // ever holds a value from a successful upload THIS session, so
      // resetting it never erases a genuine prior save.
      setValue(config.formField, undefined);
      setLocalPreviews((prev) => {
        const next = { ...prev };
        delete next[config.formField];
        return next;
      });
      setActiveObjectUrl(config.formField, null);
    } finally {
      // Only the LATEST request for this field may clear the in-flight
      // flag -- a stale request's finally must not clear it out from
      // under a newer one that is still running.
      if (!isStale()) {
        setUploadingFields((prev) => ({ ...prev, [config.formField]: false }));
      }
    }
  }

  async function handleShopFileInputChange(
    e: React.ChangeEvent<HTMLInputElement>
  ) {
    const image = e?.target?.files?.[0] ?? null;
    if (image) await runBannerUpload(image, DESKTOP_BANNER_CONFIG);
    e.target.value = '';
  }

  async function handleMobileBannerFileInputChange(
    e: React.ChangeEvent<HTMLInputElement>
  ) {
    const image = e?.target?.files?.[0] ?? null;
    if (image) await runBannerUpload(image, MOBILE_BANNER_CONFIG);
    e.target.value = '';
  }

  // GB #1612 -- Codex P2 (third review): the mobile banner is documented as
  // OPTIONAL, which has to include changing your mind. Before this, nothing
  // could ever clear store_photo_mobile once set -- every write only ever
  // assigned a new URL, and SellerProfileHeader always prioritizes this
  // field below 640px, so a seller who uploaded one was stuck with it on
  // phones no matter what they did to their desktop banner.
  //
  // Uses removeUserKey (already existing in user.service.ts), which calls
  // Firestore's deleteField() directly -- NOT a write of an empty string.
  // An empty string would still satisfy some truthiness-adjacent checks
  // depending on how they're written; true field deletion is the only way
  // to guarantee the field is actually gone, not just blank.
  //
  // GB #1612 -- Codex fifth review, finding 1: this used to call
  // removeUserKey directly, exempt from the same ordering guarantee every
  // upload gets. Seller clicks Remove, then immediately picks a new file:
  // the delete and the new upload's Firestore write were independent
  // requests with no ordering guarantee, so the delete could land LAST --
  // field gone while the UI (and the just-uploaded Storage object) show
  // the new banner. Routed through the SAME latestUploadTokenRef /
  // pendingWriteRef machinery as runBannerUpload's writes, treating
  // "remove" as just another write attempt competing for the
  // storePhotoMobile field's serialized write queue.
  async function handleRemoveMobileBanner() {
    if (!userDoc) return;
    const field = 'storePhotoMobile' as const;
    const docKey = 'store_photo_mobile' as const;

    const myToken = ++latestUploadTokenRef.current[field];
    const isStale = () => latestUploadTokenRef.current[field] !== myToken;

    // Capture what to delete from Storage BEFORE the field is cleared --
    // GB #1612 Codex fifth review, finding 4 ("most serious finding of
    // twenty"): removeUserKey only ever deleted the Firestore FIELD;
    // nothing deleted the actual Storage object, so a "removed" banner
    // stayed publicly reachable at its CDN URL forever.
    const cdnUrlToDelete =
      form.storePhotoMobile?.remoteUrl ?? userDoc.store_photo_mobile ?? null;

    setUploadingFields((prev) => ({ ...prev, [field]: true }));

    const previousWrite = pendingWriteRef.current[field];
    const thisWrite = previousWrite
      .catch(() => {
        // A prior failed write must not permanently jam the queue.
      })
      .then(() => {
        if (isStale()) return;
        return removeUserKey(userDoc.uid, docKey);
      });
    pendingWriteRef.current[field] = thisWrite;

    try {
      await thisWrite;
      if (isStale()) return; // a newer pick/remove already superseded this one

      // GB #1612 -- Codex fifth review, finding 5: refreshUserDoc()
      // returns Promise<boolean> and resolves to false (without
      // throwing) on a uid mismatch, a server-confirmed miss, or a
      // ~4s timeout (AuthContext.tsx) -- previously discarded, so a
      // failed refresh still showed "Shop successfully updated." with
      // the banner potentially still displayed from stale state.
      const refreshed = await refreshUserDoc();
      if (isStale()) return;
      if (!refreshed) {
        showErrorToast(
          'We removed your banner, but could not confirm it right away. Refresh the page to see the update.'
        );
        return;
      }

      setValue('storePhotoMobile', undefined);
      setLocalPreviews((prev) => {
        const next = { ...prev };
        delete next.storePhotoMobile;
        return next;
      });
      setActiveObjectUrl(field, null);

      // Delete the Storage object(s) too. Non-fatal to the seller-facing
      // flow if this specific step fails: the Firestore field (which
      // drives visibility everywhere -- the dashboard preview and the
      // public storefront alike) is genuinely gone either way, so the
      // seller isn't left believing something is removed when it isn't.
      // A failure here is logged loudly for follow-up, not swallowed.
      if (cdnUrlToDelete) {
        try {
          await deleteStoreBannerImage(cdnUrlToStorageFullPath(cdnUrlToDelete));
        } catch (storageErr) {
          logError(
            storageErr,
            undefined,
            `handleRemoveMobileBanner storage cleanup failed uid=${userDoc.uid}`
          );
        }
      }

      showToast(<DynamicToast text={'Shop successfully updated.'} />, {
        position: 'bottom-center',
        style: {
          borderRadius: '10px',
          backgroundColor: '#FDF4E9',
          borderStyle: 'solid',
          borderWidth: '1px',
          borderColor: '#C75008',
          width: 'min-content',
        },
      });
    } catch (err) {
      if (isStale()) return;
      showErrorToast('We could not remove your banner. Please try again.');
      logError(
        err,
        undefined,
        `handleRemoveMobileBanner failed uid=${userDoc.uid}`
      );
    } finally {
      if (!isStale()) {
        setUploadingFields((prev) => ({ ...prev, [field]: false }));
      }
    }
  }

  async function handleUserFileInputChange(
    e: React.ChangeEvent<HTMLInputElement>
  ) {
    if (!userDoc?.uid) return;

    const image = e?.target?.files?.[0] ?? null;
    if (image) {
      const localUrl = URL.createObjectURL(image);
      const fullPath = await uploadUserImage(image, userDoc.uid);
      const remoteUrl = IMAGE_PATH + fullPath + IMAGE_EXT;
      setValue('userPhoto', { localUrl, remoteUrl });
    }
  }

  const onSubmit: SubmitHandler<FormSchema> = async () => {
    if (!userDoc) return;
    // GB #1612 -- Codex P2: a banner upload still in flight (or one that
    // just failed and is being retried) must never be submittable. This is
    // belt-and-suspenders alongside the submit button's own disabled state
    // below, since Enter-to-submit can bypass a disabled button click.
    //
    // GB #1612 -- Codex fifth review, finding 7: this used to return with
    // ZERO feedback. Enter-to-submit fires before setIsSubmitting(true)
    // and before any toast, so a seller who edited bio and shipping
    // policy, picked a banner, then pressed Enter lost every one of those
    // edits with no explanation -- the form doesn't retain unsaved field
    // values across this silent no-op the way a real submit-then-refresh
    // would, so it reads as data just vanishing.
    if (isBannerUploadInFlight) {
      showErrorToast(
        'Please wait for your banner to finish uploading, then save again.'
      );
      return;
    }
    setIsSubmitting(true);

    // GB #1612 -- Codex P2: this whole body used to have no try/catch, so
    // ANY rejection here (not just the undefined-store_photo case this PR
    // fixes at the source) would throw past setIsSubmitting(false) and
    // leave "Update Shop Settings" stuck spinning forever with no
    // explanation to the seller. Fixing the root cause, not just the one
    // trigger.
    try {
      const shippingPolicy = redactPhonesAndEmailsFromStr(
        form.shippingPolicy ?? userDoc.shipping_policy ?? ''
      );
      const returnPolicy = redactPhonesAndEmailsFromStr(
        form.returnPolicy ?? userDoc.return_policy ?? ''
      );

      const bio = redactPhonesAndEmailsFromStr(form.bio ?? userDoc.bio ?? '');

      await updateUserDoc(userDoc.uid, {
        username: form.username ?? userDoc.username,
        // username_slug: form.username
        //   ? slugify(form.username)
        //   : userDoc.username_slug,
        username_changed:
          form.username !== userDoc.username
            ? Date.now()
            : userDoc.username_changed ?? 0,
        photo: form.userPhoto ? form.userPhoto.remoteUrl : userDoc.photo,
        ...(form.storePhoto && {
          store_photo: form.storePhoto
            ? form.storePhoto.remoteUrl
            : userDoc.store_photo,
        }),
        ...(form.storePhotoMobile && {
          store_photo_mobile: form.storePhotoMobile
            ? form.storePhotoMobile.remoteUrl
            : userDoc.store_photo_mobile,
        }),
        ...(form.taxStates?.length && { tax_states: form.taxStates }),
        bio,
        shipping_policy: shippingPolicy,
        return_policy: returnPolicy,
        ...(form.sellerProfile && {
          seller_profile: {
            ...((userDoc as any).seller_profile || {}),
            ...Object.fromEntries(
              Object.entries(form.sellerProfile).map(([k, v]) => [
                k,
                v?.trim() || null,
              ])
            ),
          },
        }),
      });
      showToast(<DynamicToast text={'Shop successfully updated.'} />, {
        position: 'bottom-center',
        style: {
          borderRadius: '10px',
          backgroundColor: '#FDF4E9',
          borderStyle: 'solid',
          borderWidth: '1px',
          borderColor: '#C75008',
          width: 'min-content',
        },
      });
      queryClient.invalidateQueries(['user']);
    } catch (err) {
      showErrorToast('We could not save your shop settings. Please try again.');
      logError(err, undefined, `onSubmit failed uid=${userDoc.uid}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  async function saveAddress() {
    if (!form.address) return;
    setAddressElement({ ...addressElement, loadingSave: true });
    const existingAddress = userDoc?.addresses?.find(
      (address) => address.address_line1 === form.address?.address_line1
    );
    if (!existingAddress) {
      const addresses = [form.address, ...(userDoc!.addresses ?? [])];
      addresses.forEach((address) => {
        if (address.address_line1 !== form.address?.address_line1) {
          address.is_shop_location = false;
        } else {
          address.is_shop_location = true;
        }
      });

      await updateUserByKey(userDoc!.uid, 'addresses', addresses);
    }
    await queryClient.invalidateQueries({
      queryKey: ['user'],
    });
    // change key, resets the address element, show contacts
    setAddressElement({
      key: addressElement.key + 1,
      loadingSave: false,
      showSaveButton: false,
    });
  }

  const handleAccountLink = async (field: string | 'currently_due', type: string | 'account_onboarding') => {
    setGeneratingLink(true)

    try {
      const res = await generateAccountLink(field,type)

      if (res?.url) window.open(res.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.log("Error Generating Account Link",)
      showErrorToast('There was a problem retrieving your secure link to Stripe.');
    }

    setGeneratingLink(false)
  }

  if (!userDoc) {
    return (
      <div className="m-auto h-[10rem] w-[10rem]">
        <Spinner />
      </div>
    );
  }

  // GB #1612: the "how it looks on a phone" preview shows the seller's own
  // mobile banner when they have one, otherwise falls back to the
  // center-cropped desktop banner -- exactly matching what a real phone
  // visitor sees per the storefront rendering rule in SellerProfileHeader.
  // localPreviews takes priority: it reflects an upload in progress (or
  // just-failed and reverted) that hasn't necessarily reached form state
  // yet -- see the Codex-fix comment above runBannerUpload.
  const mobileBannerSrc =
    localPreviews.storePhotoMobile ??
    form.storePhotoMobile?.localUrl ??
    userDoc.store_photo_mobile ??
    null;
  const hasMobileBannerPreview = !!mobileBannerSrc;
  const phonePreviewSrc =
    mobileBannerSrc ??
    localPreviews.storePhoto ??
    form.storePhoto?.localUrl ??
    userDoc.store_photo ??
    dWeb.src;

  const getDaysLeft = () => {
    const changedTimestamp = userDoc.username_changed;
    const today = new Date();
    const thirtyDaysFromChangedTimestamp = new Date(
      changedTimestamp + 30 * 24 * 60 * 60 * 1000
    );
    const daysLeft =
      Math.floor(
        (thirtyDaysFromChangedTimestamp.getTime() - today.getTime()) /
          (1000 * 3600 * 24)
      ) + 1;
    return daysLeft;
  };

  const getDescription = () => {
    if (canChangeUserName(userDoc)) {
      return "e.g Ryan&apos;s Gear Shop, Good Camera, etc";
    }
    return `You can change your username in ${getDaysLeft()} days`;
  };

  const toggleVacation = async () => {
    const turningOff = userDoc.on_vacation;
    const targetOnVacation = !userDoc.on_vacation;
    try {
      const text = turningOff
        ? 'Are you sure you want to turn off vacation mode? This will make all your listings visible again.'
        : 'Are you sure you want to turn on vacation mode? This will temporarily remove all your listings until turned back on.';
      const yes = confirm(text);
      if (yes) {
        setLoadingVacation(true);
        const updated = await setVacationMode(userDoc.uid, targetOnVacation);
        // GB #1471: the checkbox's checked state comes exclusively from the
        // AuthContext onSnapshot listener, which can stall (e.g. a
        // backgrounded tab or a flaky connection). Force a server read so
        // the toggle already reflects this write by the time the success
        // toast tells the user it worked. The invalidateQueries(['user'])
        // call that used to sit here prefix-matched only ['user', <uid>]
        // queries for OTHER users (chat partners, order counterparties) —
        // none of which feed this checkbox — so it never refreshed it.
        await refreshUserDoc();
        if (turningOff) {
          showSuccessToast('Vacation mode turned off.');
        } else if (updated) {
          showSuccessToast(`Successfully hid ${updated} listings.`);
        } else {
          showSuccessToast('Vacation mode turned on.');
        }
      }
    } catch (e) {
      showErrorToast(
        (e as Error)?.message || 'Failed to update vacation mode. Please try again.'
      );
      logError(
        e,
        undefined,
        `toggleVacation failed uid=${userDoc.uid} targetOnVacation=${targetOnVacation}`
      );
    }
    setLoadingVacation(false);
  };

  const toggleOfferSettings = async () => {
    try {
      setLoadingOfferSettings(true);
      const currentValue = userDoc.offer_settings?.default_enabled ?? true;
      await updateUserDoc(userDoc.uid, {
        offer_settings: {
          default_enabled: !currentValue,
        },
      });
      queryClient.invalidateQueries(['user']);
      showSuccessToast(
        `Make Offer is now ${!currentValue ? 'enabled' : 'disabled'} by default for new listings`
      );
    } catch (e) {
      showErrorToast('Failed to update offer settings');
      console.error(e);
    }
    setLoadingOfferSettings(false);
  };

  const requirementTexts = {
    // --- Original 7 entries (do not remove) ---
    'external_account': "Bank Account: You must have a linked bank account to receive payouts from your sales.",
    'individual_verification_document': "Photo ID: For fraud protection, a copy of a government-issued photo ID is required to verify your identity manually.",
    'individual.id_number': 'ID Number: For fraud protection, a valid government-issued identification number is required to verify your identity manually.',
    'individual.address.line1': 'Address: Please provide your complete residential address, including line 1. This is required to verify your identity and ensure accurate communication regarding your account.',
    'tos_acceptance.ip': "Terms Of Service: To finalize your account setup, verify your IP address by accepting the terms of service with Stripe.",
    'tos_acceptance.date': "Terms Of Service: Your account setup is pending completion with Stripe. Ensure you've accepted the terms of service.",
    'company.tax_id': 'Tax ID: For businesses, a valid tax identification number is required. This helps with tax reporting and compliance, ensuring accurate records for your company.',

    // --- Payment settings ---
    'settings.payments.statement_descriptor': "Statement Descriptor: A short label (5–22 characters) that appears on your buyer's credit card statement. It does NOT need to be a business name even if Stripe's form mentions one — your own name, your shop name, or \"GEARFOCUS\" all work.",
    'settings.card_payments.statement_descriptor_prefix': "Card Statement Prefix: A short prefix (2–10 characters) shown before the full statement descriptor on card receipts. Choose something simple and recognizable, like your initials or a short shop name abbreviation.",

    // --- Business profile ---
    'business_profile.url': "Website or Online Profile: Stripe wants to see that you have an online selling presence. Your GearFocus shop page works, as does any social profile (Instagram, Facebook, YouTube) where you sell or showcase gear.",
    'business_profile.mcc': "Business Category: Stripe needs to know what type of business you run. For most GearFocus sellers, this will be set automatically. If prompted, select \"Cameras & Photography\" or the closest match.",
    'business_profile.product_description': "What You Sell: One or two sentences describing the gear you sell — for example, \"Used cameras, lenses, and accessories.\" This is only seen by Stripe; it doesn't appear on your shop.",

    // --- Individual identity ---
    'individual.first_name': "First Name: Your legal first name, exactly as it appears on your government-issued ID.",
    'individual.last_name': "Last Name: Your legal last name, exactly as it appears on your government-issued ID.",
    'individual.email': "Email Address: A valid email address for your Stripe seller account. This is used for Stripe account communications.",
    'individual.phone': "Phone Number: A valid phone number for your Stripe account. Stripe may use this for verification or account security alerts.",

    // --- Date of birth ---
    'individual.dob.day': "Date of Birth (Day): The day portion of your date of birth, for identity verification purposes.",
    'individual.dob.month': "Date of Birth (Month): The month portion of your date of birth, for identity verification purposes.",
    'individual.dob.year': "Date of Birth (Year): The year portion of your date of birth, for identity verification purposes.",

    // --- SSN / tax ---
    'individual.ssn_last_4': "Last 4 of SSN: The last four digits of your Social Security Number. Stripe uses this to verify your identity for U.S. tax and federal compliance.",

    // --- Role / title ---
    'individual.relationship.title': "Your Job Title: Your title or role at the business (e.g., \"Owner\", \"Sole Proprietor\", or \"Photographer\"). This is required by Stripe to confirm your relationship to the account.",

    // --- Identity verification ---
    'individual.verification.proof_of_liveness': "Selfie Verification: Stripe needs a quick selfie to confirm your photo ID matches you. Click \"Start Process\" and follow the steps in Stripe's secure window — it takes about a minute. This is a Stripe identity check, not a GearFocus one.",
    'individual.verification.document': "Photo ID Upload: Stripe needs a photo of a government-issued ID (driver's license or passport) to verify your identity. This is a routine check, not anything specific to your account. Click \"Start Process\" to upload it through Stripe's secure form.",

    // --- Individual address ---
    'individual.address.line2': "Address Line 2: The second line of your residential address, if applicable (apartment, suite, or unit number).",
    'individual.address.city': "City: The city of your residential address, required for identity verification.",
    'individual.address.state': "State: The state or province of your residential address, required for identity verification.",
    'individual.address.postal_code': "ZIP / Postal Code: Your residential ZIP or postal code, required for identity verification.",
    'individual.address.country': "Country: The country of your residential address. For U.S. sellers this is typically \"US\".",

    // --- Terms of service representative ---
    'tos_acceptance.account_representative.date': "Terms of Service Acceptance Date: Stripe requires confirmation of when you accepted their terms of service. Click \"Start Process\" to complete this step.",

    // --- Japan-specific fields (native-script names and addresses) ---
    'individual.first_name_kana': "First Name (Kana): This is a Japan-specific field. Please enter your first name in katakana or hiragana as it appears on your Japanese ID.",
    'individual.first_name_kanji': "First Name (Kanji): This is a Japan-specific field. Please enter your first name in kanji as it appears on your Japanese ID.",
    'individual.last_name_kana': "Last Name (Kana): This is a Japan-specific field. Please enter your last name in katakana or hiragana as it appears on your Japanese ID.",
    'individual.last_name_kanji': "Last Name (Kanji): This is a Japan-specific field. Please enter your last name in kanji as it appears on your Japanese ID.",
    'company.address_kana.city': "Company City (Kana): This is a Japan-specific field. Please enter your company city in katakana or hiragana.",
    'company.address_kana.line1': "Company Address (Kana): This is a Japan-specific field. Please enter your company street address in katakana or hiragana.",
    'company.address_kana.postal_code': "Company Postal Code (Kana): This is a Japan-specific field. Please enter your company postal code in katakana or hiragana format.",
    'company.address_kana.state': "Company State/Prefecture (Kana): This is a Japan-specific field. Please enter your company prefecture in katakana or hiragana.",
    'company.address_kana.town': "Company Town (Kana): This is a Japan-specific field. Please enter your company town or district in katakana or hiragana.",
    'company.address_kanji.city': "Company City (Kanji): This is a Japan-specific field. Please enter your company city in kanji.",
    'company.address_kanji.line1': "Company Address (Kanji): This is a Japan-specific field. Please enter your company street address in kanji.",
    'company.address_kanji.postal_code': "Company Postal Code (Kanji): This is a Japan-specific field. Please enter your company postal code in kanji.",
    'company.address_kanji.state': "Company State/Prefecture (Kanji): This is a Japan-specific field. Please enter your company prefecture in kanji.",
    'company.address_kanji.town': "Company Town (Kanji): This is a Japan-specific field. Please enter your company town or district in kanji.",
    'company.name_kana': "Company Name (Kana): This is a Japan-specific field. Please enter your company name in katakana or hiragana.",
    'company.name_kanji': "Company Name (Kanji): This is a Japan-specific field. Please enter your company name in kanji.",
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {/* GB #1799: mounted only while actually open. BaseModal calls
          useRouter() unconditionally (for its back-navigation dismiss
          behavior), so an always-mounted-but-closed instance would pull a
          router dependency into every render of this page for a feature
          that only matters right after a desktop banner upload. */}
      {showMobileBannerChoice && (
        <MobileBannerChoiceModal
          isOpen={showMobileBannerChoice}
          previewSrc={mobileBannerChoicePreview ?? dWeb.src}
          onAddMobileBanner={() => {
            setShowMobileBannerChoice(false);
            document.getElementById('storePhotoMobile')?.click();
          }}
          onKeepDesktopImage={() => {
            // GB #1799 -- Bill's ruling: don't ask again in the same
            // sitting once the seller has explicitly declined.
            hasDeclinedMobileBannerThisSessionRef.current = true;
            setShowMobileBannerChoice(false);
          }}
        />
      )}
      <div className="flex h-[8rem] w-full items-center bg-brand-white px-[1.6rem] py-[0.8rem]">
        <h1 className="text-[2.4rem] font-semibold leading-[3.2rem] text-brand-lightest-black">
          Shop Settings
        </h1>
      </div>
      <Divider />
      <div className="flex w-full flex-col items-start gap-[0.8rem]">
        {(
          (!!userDoc.stripe?.requirements?.errors && userDoc.stripe.requirements.errors.length > 0)
        ) && (
          <div className="flex w-full flex-col items-start lg:rounded-[10px] lg:border-2 mb-5">
            <div className="flex w-full items-center justify-between gap-[0.8rem] p-[1.6rem] lg:border-b-2">
              <div className="flex items-center gap-[0.8rem]">
                <WarningIcon />
                <h3 className="text-[1.8rem] font-semibold leading-[2.7rem]">
                  Account Verification Errors
                </h3>
              </div>
            </div>
            <div className="bg-red-100 text-red-800 p-6 rounded-lg shadow-md text-[1.4rem] w-full">
              <ol className="list-decimal pl-6 mb-6">
                {(
                  userDoc.stripe?.requirements?.errors?.map((error) => (
                    <li className="my-1" key={error.code}>
                      {error.reason && error.reason.trim().length > 0
                        ? error.reason
                        : "An error was reported on your Stripe account. Please contact support@gearfocus.com."}
                    </li>
                  ))
                )}
              </ol>
            </div>
          </div>
        )}

        {(
          (!!userDoc.stripe?.requirements?.currently_due && userDoc.stripe.requirements.currently_due.length > 0) ||
          (!!userDoc.stripe?.requirements?.eventually_due && userDoc.stripe.requirements.eventually_due.length > 0)
        ) && (
          <div className="flex w-full flex-col items-start lg:rounded-[10px] lg:border-2 mb-5">
            <div className="flex w-full items-center justify-between gap-[0.8rem] p-[1.6rem] lg:border-b-2">
              <div className="flex items-center gap-[0.8rem]">
                <WarningIcon />
                <h3 className="text-[1.8rem] font-semibold leading-[2.7rem]">
                  {userDoc.stripe?.requirements?.currently_due?.length ? "Incomplete Account Information" : "Eventually Required Information"}
                </h3>
              </div>
              {isGeneratingLink ? (
                <Button
                  type="secondary"
                  height="small"
                  leadingIcon={<Spinner />}
                  text="Generating Secure Connection..."
                  disabled
                />
              ) : (
                <Button
                  type="secondary"
                  height="small"
                  text="Start Process"
                  onClick={() => {handleAccountLink(userDoc.stripe?.requirements?.currently_due?.length ? 'currently_due' : 'eventually_due', 'account_onboarding')}}
                />
              )}
            </div>
            <div className="bg-yellow-100 text-yellow-800 p-6 rounded-lg shadow-md text-[1.4rem]">
              <p className="mb-4">
                We need a few more details before you can start receiving payouts. Please complete the items below:
              </p>
              <ol className="list-decimal pl-6 mb-6">
                {(
                  userDoc.stripe?.requirements?.currently_due?.map((requirement) => (
                    <li className="my-1" key={requirement}>
                      {requirementTexts[requirement as keyof typeof requirementTexts] ??
                        "Additional information: Stripe needs more details about your account. Click \"Start Process\" and follow the prompts in Stripe's secure window to complete this requirement."}
                    </li>
                  )) ||
                  userDoc.stripe?.requirements?.eventually_due?.map((requirement) => (
                    <li className="my-1" key={requirement}>
                      {requirementTexts[requirement as keyof typeof requirementTexts] ??
                        "Additional information: Stripe needs more details about your account. Click \"Start Process\" and follow the prompts in Stripe's secure window to complete this requirement."}
                    </li>
                  ))
                )}
              </ol>
            </div>
          </div>
        )}

        <div className="flex w-full flex-col items-center lg:rounded-[10px] lg:border-2 ">
          {/* Banner image -- GB #1612 (Codex P2, fourth review): sharing
              only SellerProfileHeader's fixed-height classes (the third-
              review fix) was still wrong, because this page's content
              column is narrower than the seller's real viewport --
              DashboardLayout subtracts a 26rem side menu plus 12rem
              horizontal padding at lg+ widths. Same height, narrower
              width = a different object-cover crop (measured: 1024px
              viewport -> storefront 1024x300 = 3.41:1, this column only
              524x300 = 1.75:1 with the height-only fix). Applying
              getStorefrontBannerAspectRatio(viewportWidth) as an inline
              aspect-ratio reproduces the storefront's exact crop
              proportions regardless of this box's own physical width --
              verified the same way, by real-browser measurement, not by
              reasoning about the classes. */}
          <div
            className="relative flex w-full items-center justify-center gap-[1rem] bg-brand-lighter-gray"
            style={{ aspectRatio: String(desktopPreviewAspectRatio) }}
          >
            <div className="absolute z-10">
              <Button
                type="secondary"
                leadingIcon={<PencilSimpleIcon />}
                width="small"
                text="Change Shop Banner"
                onClick={() => {
                  // open file input
                  document.getElementById('storePhoto')?.click();
                }}
              />
            </div>
            <input
              id="storePhoto"
              type="file"
              name="storePhoto"
              accept="image/png,image/jpeg,image/webp"
              hidden
              onChange={handleShopFileInputChange}
            />

            <SafeImage
              className="h-full w-full rounded-t-[10px] object-cover"
              alt={'shop banner'}
              fill
              priority
              key={
                localPreviews.storePhoto ??
                form.storePhoto?.localUrl ??
                userDoc.store_photo ??
                'default'
              }
              src={
                localPreviews.storePhoto ??
                form.storePhoto?.localUrl ??
                userDoc.store_photo ??
                dWeb.src
              }
            />
          </div>
          <InputRow
            label="Banner image"
            description={
              <>
                Upload at{' '}
                <strong className="font-semibold">1600 x 360 pixels</strong>{' '}
                for the sharpest result. This is what buyers see on a
                computer or tablet. Add a mobile banner below if you want
                full control over what phone visitors see. Without one,
                phones will show the middle of this image. Keep logos and
                text inside the middle 60% of the image -- your shop
                details cover the bottom.
                <br />
                <br />
                <a
                  href="https://gearfocus.b-cdn.net/seller-resources/gf-banner-template-1600x360.png"
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-secondary hover:text-brand-primary"
                >
                  Download banner template
                </a>
                {' · '}
                <Link
                  href="/help?search=banner"
                  className="text-brand-secondary hover:text-brand-primary"
                >
                  See size guide
                </Link>
              </>
            }
          >
            <div className="flex flex-col items-start gap-[0.8rem]">
              {/* GB #1612 -- Codex fifth review, finding 11: this hardcoded
                  a literal aspect-[39/20] instead of deriving from
                  bannerDimensions.ts -- the exact drift that file's own
                  comment says it exists to prevent, in the single most
                  important preview of a mobile-first feature. */}
              <div
                className="relative w-full max-w-[390px] overflow-hidden rounded-[10px] bg-brand-lighter-gray"
                style={{ aspectRatio: String(PHONE_PREVIEW_ASPECT_RATIO) }}
              >
                <SafeImage
                  className="h-full w-full object-cover"
                  alt={
                    hasMobileBannerPreview
                      ? 'Mobile banner preview'
                      : 'Banner preview on a phone'
                  }
                  fill
                  key={phonePreviewSrc}
                  src={phonePreviewSrc}
                />
              </div>
              <span className="text-[1.3rem] text-brand-dark-gray">
                {hasMobileBannerPreview
                  ? 'This is your mobile banner'
                  : 'How your banner looks on a phone'}
              </span>
            </div>
          </InputRow>
          <InputRow
            label="Mobile banner"
            description={
              <>
                Optional. Upload at{' '}
                <strong className="font-semibold">800 x 400 pixels</strong>{' '}
                for the sharpest result on a phone. Keep your logo and text
                inside the middle 40% of the image -- your shop details
                cover the bottom. If you skip this, phones will show the
                middle of your banner above instead.
                <br />
                <br />
                <a
                  href="https://gearfocus.b-cdn.net/seller-resources/gf-banner-template-800x400.png"
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-secondary hover:text-brand-primary"
                >
                  Download mobile banner template
                </a>
                {' · '}
                <Link
                  href="/help?search=banner"
                  className="text-brand-secondary hover:text-brand-primary"
                >
                  See size guide
                </Link>
              </>
            }
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:gap-[1.6rem]">
              <div className="relative aspect-[2/1] w-[16rem] overflow-hidden rounded-[10px] bg-brand-lighter-gray">
                {mobileBannerSrc && (
                  <SafeImage
                    className="h-full w-full object-cover"
                    alt="mobile banner"
                    fill
                    key={mobileBannerSrc}
                    src={mobileBannerSrc}
                  />
                )}
              </div>
              <div className="flex items-center gap-[1.2rem] font-semibold text-brand-secondary">
                <Button
                  type="text"
                  leadingIcon={<PencilSimpleIcon />}
                  text="Change Mobile Banner"
                  width={'extraSmall'}
                  noXPadding
                  height="extraSmall"
                  onClick={() => {
                    // open file input
                    document.getElementById('storePhotoMobile')?.click();
                  }}
                />
                {/* GB #1612 -- Codex P2 (third review): "Optional" has to
                    include changing your mind. Label is NOT Bill-approved
                    copy -- deliberately minimal ("Remove", no explanatory
                    sentence) pending his sign-off. */}
                {!!mobileBannerSrc && (
                  <Button
                    type="text"
                    text="Remove"
                    width={'extraSmall'}
                    noXPadding
                    height="extraSmall"
                    disabled={!!uploadingFields.storePhotoMobile}
                    onClick={handleRemoveMobileBanner}
                  />
                )}
                <input
                  id="storePhotoMobile"
                  type="file"
                  hidden
                  name="storePhotoMobile"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleMobileBannerFileInputChange}
                />
              </div>
            </div>
          </InputRow>
          <InputRow
            label="Shop Photo"
            description=" Using the right photos for your shop will make a positive
                                    impression on customers. This is also your profile photo."
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:gap-[1.6rem]">
              {userDoc && (
                <SafeImage
                  className="h-[16rem] w-[16rem] rounded-[10px] object-contain"
                  width={160}
                  height={160}
                  alt={'user photo'}
                  unoptimized
                  key={form.userPhoto?.localUrl ?? userDoc.photo}
                  src={form.userPhoto?.localUrl ?? userDoc.photo}
                />
              )}

              <div className="font-semibold text-brand-secondary">
                <Button
                  type="text"
                  leadingIcon={<PencilSimpleIcon />}
                  text="Change Shop Photo"
                  width={'extraSmall'}
                  noXPadding
                  height="extraSmall"
                  onClick={() => {
                    // open file input
                    document.getElementById('userPhoto')?.click();
                  }}
                />
                <input
                  type="file"
                  hidden
                  name="userPhoto"
                  id="userPhoto"
                  onChange={handleUserFileInputChange}
                />
              </div>
            </div>
          </InputRow>
          <InputRow label="Shop Name" description={getDescription()}>
            {canChangeUserName(userDoc) ? (
              <FormInput
                {...register('username')}
                defaultValue={userDoc.username}
              />
            ) : (
              <span className="text-brand-secondary">{userDoc.username}</span>
            )}
          </InputRow>
          <InputRow label="Shop Address Location">
            <Elements
              options={STRIPE_ELEMENT_OPTIONS}
              stripe={stripePromise}
              key={addressElement.key}
            >
              <StripeAddressForm
                onChange={(e: StripeAddressChangeEvent) => {
                  if (e.status === 'incomplete') {
                    setAddressElement({
                      ...addressElement,
                      showSaveButton: e.result.isNewAddress,
                    });
                    setValidatedAddress(null);
                    return;
                  }
                  if (e.status === 'validating') {
                    setValidating(true);
                    return;
                  }
                  setValidating(false);
                  if (e.status === 'valid') {
                    setValue('address', e.result);
                    setValidatedAddress(e.result);
                  } else {
                    setValidatedAddress(null);
                  }
                }}
                mode={'shipping'}
                // show shop location first
                addresses={userDoc?.addresses?.sort((a) =>
                  a.is_shop_location ? -1 : 1
                )}
              ></StripeAddressForm>
            </Elements>
            {addressElement.showSaveButton && (
              <div className="mt-[1.2rem] flex justify-end">
                <Button
                  type="secondary"
                  buttonType="button"
                  text="Save Address"
                  disabled={validatedAddress === null || validating}
                  loading={addressElement.loadingSave || validating}
                  onClick={() => saveAddress()}
                />
              </div>
            )}
          </InputRow>
          <InputRow
            label="About Shop"
            description="Tell customers more about your shop"
          >
            <FormTextarea
              placeholder="Describe your shop ..."
              defaultValue={userDoc.bio}
              {...register('bio')}
            />
          </InputRow>
          <InputRow
            label="Vacation Mode"
            description="Hide your listings while this is checked"
          >
            {loadingVacation ? (
              <div className="my-auto h-[4rem] w-[4rem]">
                <Spinner />
              </div>
            ) : (
              <Checkbox
                label={'Use Vacation Mode'}
                selected={!!userDoc.on_vacation}
                onChange={toggleVacation}
              />
            )}
          </InputRow>
          <InputRow
            label="Make Offer Default"
            description="When checked, new listings will allow buyers to make offers. You can override this per listing."
          >
            {loadingOfferSettings ? (
              <div className="my-auto h-[4rem] w-[4rem]">
                <Spinner />
              </div>
            ) : (
              <Checkbox
                label={'Enable Make Offer on new listings'}
                selected={userDoc.offer_settings?.default_enabled ?? true}
                onChange={toggleOfferSettings}
              />
            )}
          </InputRow>
          <StoreSaleSettings sellerId={userDoc.uid} renderPanel={(panel) => (
            <InputRow label="Store sale" description="Offer one percentage off your eligible listings for a limited time.">
              {panel}
            </InputRow>
          )} />
          <InputRow
            label="US Tax Calculation"
            description="GearFocus is legally required to collect and remit taxes for the following Marketplace Facilitator states"
          >
            <>
              <TaxStateList />
            </>
          </InputRow>
          {userDoc.business_type === 'company' && (
            <InputRow
              label="Specify US States you collect and remit"
              description="Select one of the states if you're from US"
            >
              <FormSelect<FormSchema>
                options={[...TAX_STATES]}
                name="taxStates"
                placeholder="Choose states..."
                control={control}
                multiple
                allowEmpty={false}
                defaultValue={userDoc.tax_states || []}
              />
            </InputRow>
          )}
          {userDoc.account_id && (
            <InputRow
              label="Update Stripe Account"
              description="Generate a secure link with Stripe to update your contact information or payout bank account."
            >
              <div className="flex w-full">
                {isGeneratingLink ? (
                  <Button
                    type="text"
                    height="small"
                    leadingIcon={<Spinner />}
                    text="Generating Secure Connection..."
                    disabled
                  />
                ) : (
                  <Button
                    type="text"
                    height="small"
                    text="Update Account"
                    onClick={() => {handleAccountLink(userDoc.stripe?.requirements?.currently_due?.length ? 'currently_due' : 'eventually_due', 'account_update')}}
                  />
                )}
              </div>
            </InputRow>
          )}
        </div>
        <Divider />
        <div className="flex w-full flex-col items-start lg:rounded-[10px] lg:border-2">
          <div className="flex w-full items-center gap-[0.8rem] p-[1.6rem] lg:border-b-2">
            <SecuritySafeIcon />
            <h3 className="text-[1.8rem] font-semibold leading-[2.7rem]">
              Social Media & Profiles
            </h3>
          </div>
          <div className="space-y-2 px-[1.6rem] py-[1.2rem]">
            <p className="mb-4 text-[1.3rem] text-gray-500">
              Help buyers find you and strengthen your verified seller profile.
            </p>
            <InputRow label="eBay Username">
              <FormInput
                defaultValue={(userDoc as any).seller_profile?.ebay_username ?? ''}
                placeholder="e.g., johncamera42"
                {...register('sellerProfile.ebay_username')}
              />
            </InputRow>
            <InputRow label="Instagram">
              <FormInput
                defaultValue={(userDoc as any).seller_profile?.instagram ?? ''}
                placeholder="@yourhandle"
                {...register('sellerProfile.instagram')}
              />
            </InputRow>
            <InputRow label="YouTube">
              <FormInput
                defaultValue={(userDoc as any).seller_profile?.youtube ?? ''}
                placeholder="youtube.com/@yourchannel"
                {...register('sellerProfile.youtube')}
              />
            </InputRow>
            <InputRow label="Website">
              <FormInput
                defaultValue={(userDoc as any).seller_profile?.website ?? ''}
                placeholder="yoursite.com"
                {...register('sellerProfile.website')}
              />
            </InputRow>
            <InputRow label="TikTok">
              <FormInput
                defaultValue={(userDoc as any).seller_profile?.tiktok ?? ''}
                placeholder="@yourhandle"
                {...register('sellerProfile.tiktok')}
              />
            </InputRow>
            <InputRow label="LinkedIn">
              <FormInput
                defaultValue={(userDoc as any).seller_profile?.linkedin ?? ''}
                placeholder="linkedin.com/in/yourprofile"
                {...register('sellerProfile.linkedin')}
              />
            </InputRow>
            <InputRow label="Facebook">
              <FormInput
                defaultValue={(userDoc as any).seller_profile?.facebook ?? ''}
                placeholder="facebook.com/yourpage"
                {...register('sellerProfile.facebook')}
              />
            </InputRow>
            <InputRow label="FredMiranda">
              <FormInput
                defaultValue={(userDoc as any).seller_profile?.fredmiranda ?? ''}
                placeholder="Your FM username"
                {...register('sellerProfile.fredmiranda')}
              />
            </InputRow>
          </div>
        </div>

        <div className="overflow-hidden rounded-[1.2rem] border-2 border-brand-lightest-gray lg:max-w-[67.4rem]">
          <div className="flex w-full items-center gap-[0.8rem] p-[1.6rem] lg:border-b-2">
            <SecuritySafeIcon />
            <h3 className="text-[1.8rem] font-semibold leading-[2.7rem]">
              Shop Policies
            </h3>
          </div>
          <InputRow label="Shipping Info">
            <FormTextarea
              defaultValue={
                form.shippingPolicy ?? userDoc.shipping_policy ?? ''
              }
              {...register('shippingPolicy')}
            />
          </InputRow>
          {/*<InputRow label="Return Policy">
            <FormTextarea
              defaultValue={form.returnPolicy ?? userDoc.return_policy ?? ''}
              {...register('returnPolicy')}
            />
          </InputRow>*/}
        </div>
        <div className="flex w-full justify-end px-4 pb-4">
          <Button
            buttonType="submit"
            text="Update Shop Settings"
            loading={isSubmitting}
            disabled={isSubmitting || isBannerUploadInFlight}
          />
        </div>
      </div>
    </form>
  );
};

export default ShopSettingsPage;
